/* Geschatte bezorgdatum op de PDP - herschreven 31-08-2026.
 *
 * Wat er mis was met de vorige versie (build-output, 19.504 bytes):
 *
 * 1. HARDCODED JAARTAL. De feestdagen kwamen van
 *    date.nager.at/api/v3/publicholidays/2025/NL. Op 31-08-2026 rekende de
 *    winkel dus met de kalender van vorig jaar. Nu: lopend en volgend jaar,
 *    zodat het rond de jaarwisseling ook klopt.
 *
 * 2. MORGEN WERD NIET GETOETST. De code keek of VANDAAG een feestdag was en
 *    toonde anders zonder meer Morgen bezorgd. Op 4 mei beloofde de PDP dus
 *    bezorging op Bevrijdingsdag. Nu wordt morgen ook getoetst.
 *
 * 3. TIJDZONEFOUT. De feestdagvergelijking gebruikte toISOString(), dat in
 *    UTC rekent. Tussen middernacht en 02:00 Nederlandse zomertijd viel de
 *    datum een dag terug en werd de verkeerde dag getoetst.
 *
 * 4. AXIOS. 17 van de 19,5 KB was een volledige kopie van axios 0.24.0, voor
 *    exact een GET-verzoek. Vervangen door fetch. Het bestand is nu ~4 KB.
 *    De feestdagenlijst wordt bovendien 7 dagen gecachet, dus niet elke
 *    PDP-weergave doet dat externe verzoek nog.
 *
 * BEWUST NIET GEWIJZIGD: de zaterdagregel. Vrijdag voor 21:00 toont nog
 * steeds Morgen bezorgd, dus zaterdaglevering. Of dat klopt is een
 * bedrijfsbeslissing en geen bug - de announcement bar zegt ma t/m vr.
 *
 * LET OP: dit is build-output uit de bronrepo. Een deploy vanuit die repo
 * zet de oude versie terug, inclusief alle vier de fouten hierboven.
 */
(() => {
  const CUTOFF_HOUR = 21;
  const CUTOFF_MINUTES = 0;
  const CACHE_KEY = 'tam_holidays_nl';
  const CACHE_TTL = 7 * 24 * 60 * 60 * 1000;

  const localDate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const addDays = (d, n) => { const c = new Date(d); c.setDate(c.getDate() + n); return c; };
  const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
  const format = (d) => d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });

  const readCache = () => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const c = JSON.parse(raw);
      if (!c || Date.now() - c.t > CACHE_TTL) return null;
      if (c.y !== new Date().getFullYear()) return null;
      return c.d;
    } catch (e) {
      return null;
    }
  };

  const writeCache = (dates) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), y: new Date().getFullYear(), d: dates }));
    } catch (e) {
      /* prive-modus of vol: cache overslaan, verder niets aan de hand */
    }
  };

  const fetchYear = async (year) => {
    const r = await fetch(`https://date.nager.at/api/v3/publicholidays/${year}/NL`);
    if (!r.ok) throw new Error(`nager ${r.status}`);
    return (await r.json()).map((h) => h.date);
  };

  const getHolidays = async () => {
    const cached = readCache();
    if (cached) return cached;
    const year = new Date().getFullYear();
    try {
      const both = await Promise.all([fetchYear(year), fetchYear(year + 1)]);
      const dates = both.flat();
      writeCache(dates);
      return dates;
    } catch (e) {
      console.warn('Feestdagen ophalen mislukt; weekendlogica blijft werken', e);
      return [];
    }
  };

  class EstimatedShipping extends HTMLElement {
    constructor() {
      super();
      this.holidays = [];
      this.timer = null;
    }

    connectedCallback() {
      this.target = this.querySelector('.shipping-date-pdp');
      if (!this.target) return;
      this.init();
    }

    disconnectedCallback() {
      if (this.timer) clearInterval(this.timer);
    }

    async init() {
      this.holidays = await getHolidays();
      this.calculate();
      this.timer = setInterval(() => this.calculate(), 60000);
    }

    isHoliday(d) {
      return this.holidays.includes(localDate(d));
    }

    nextDeliveryDay(from) {
      let d = from;
      while (isWeekend(d) || this.isHoliday(d)) d = addDays(d, 1);
      return d;
    }

    calculate() {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const afterCutoff = hours > CUTOFF_HOUR || (hours === CUTOFF_HOUR && minutes >= CUTOFF_MINUTES);

      /* Vandaag gaat er niets meer de deur uit: weekend of feestdag. */
      if (isWeekend(now) || this.isHoliday(now)) {
        this.show(`Bezorging ${format(this.nextDeliveryDay(addDays(now, 1)))}`, 0);
        return;
      }

      /* Na de cutoff: morgen verzenden, bezorging op de eerstvolgende bezorgdag. */
      if (afterCutoff) {
        this.show(`Bezorging ${format(this.nextDeliveryDay(addDays(now, 1)))}`, 0);
        return;
      }

      /* Voor de cutoff. Alleen doorschuiven als morgen een feestdag is; de
         zaterdagregel blijft bewust ongemoeid. */
      const left = (CUTOFF_HOUR - hours) * 60 + (CUTOFF_MINUTES - minutes);
      const tomorrow = addDays(now, 1);
      if (this.isHoliday(tomorrow)) {
        this.show(`Bezorging ${format(this.nextDeliveryDay(tomorrow))}`, left);
      } else {
        this.show('Morgen bezorgd', left);
      }
    }

    show(message, minutesLeft) {
      if (minutesLeft > 0) {
        const h = Math.floor(minutesLeft / 60);
        const m = minutesLeft % 60;
        this.target.innerHTML = `${message}. Bestel binnen ${h} u ${m} min`;
      } else {
        this.target.innerHTML = message;
      }
    }
  }

  if (customElements.get('estimated-shipping') === undefined) {
    customElements.define('estimated-shipping', EstimatedShipping);
  }
})();
